import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import NREMTAssessmentPreview from './NREMTAssessmentPreview';

const NREMTAssessmentList = () => {
  console.log('🚨 NREMT DASHBOARD STARTING UP!!! 🚨');
  
  const { currentUser, userRole } = useAuth();
  const { permissions, loading: permissionsLoading } = useInstructorPermissions(currentUser?.uid);
  const navigate = useNavigate();
  
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [previewAssessment, setPreviewAssessment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Determine navigation paths based on user role
  const getDashboardPath = () => {
    return (userRole === 'Admin' || userRole === 'owner') ? '/admin' : '/instructor-dashboard';
  };

  const getCreatePath = () => {
    return (userRole === 'Admin' || userRole === 'owner') ? '/nremt-builder/create' : '/instructor/nremt-builder/create';
  };

  const getEditPath = (templateId) => {
    return (userRole === 'Admin' || userRole === 'owner') ? `/nremt-builder/edit/${templateId}` : `/instructor/nremt-builder/edit/${templateId}`;
  };

  const getPrintPath = (templateId) => {
    return (userRole === 'Admin' || userRole === 'owner') ? `/nremt-print/${templateId}` : `/instructor/nremt-print/${templateId}`;
  };

  const getPrintBuilderPath = () => {
    return (userRole === 'Admin' || userRole === 'owner') ? '/nremt-print-builder/create' : '/instructor/nremt-print-builder/create';
  };

  const getManualSheetPath = () => {
    return (userRole === 'Admin' || userRole === 'owner') ? '/nremt-assessment-sheet' : '/instructor/nremt-assessment-sheet';
  };

  // Assessment type options
  const assessmentTypes = [
    'Practical Skill',
    'Patient Assessment',
    'Scenario Based',
    'Airway Management',
    'Cardiac Arrest',
    'Medical Emergency',
    'Trauma Emergency',
    'Other'
  ];

  // Fetch assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      console.log('🔍 NREMTAssessmentList: Fetching assessments...');
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'nremtAssessments'));
        console.log('🔍 NREMTAssessmentList: Found', querySnapshot.docs.length, 'documents');
        
        const assessmentsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('🔍 NREMTAssessmentList: Document', doc.id, ':', data);
          return {
            id: doc.id,
            ...data
          };
        });
        
        console.log('🔍 NREMTAssessmentList: Final assessments array:', assessmentsData);
        setAssessments(assessmentsData);
      } catch (error) {
        console.error('Error fetching NREMT assessments:', error);
      }
      setLoading(false);
    };

    fetchAssessments();
  }, []);

  // Filter assessments
  const filteredAssessments = assessments.filter(assessment => {
    const matchesSearch = assessment.name?.toLowerCase().includes(search.toLowerCase()) ||
                         assessment.description?.toLowerCase().includes(search.toLowerCase());
    const matchesType = !selectedType || assessment.assessmentType === selectedType;
    return matchesSearch && matchesType;
  });

  // Permission checks
  const hasGeneralAccess = userRole === 'Admin' || userRole === 'owner' || 
                          (userRole === 'Instructor' && permissions?.skillSheets !== 'none');
  const hasSpecialAccess = userRole === 'Admin' || userRole === 'owner' || 
                          (userRole === 'Instructor' && permissions?.skillSheets === 'special');
  const canCreate = hasGeneralAccess;
  const canEdit = hasSpecialAccess;
  const canDelete = hasSpecialAccess;

  // Handle delete
  const handleDelete = async (assessmentId) => {
    if (!canDelete) return;
    
    try {
      await deleteDoc(doc(db, 'nremtAssessments', assessmentId));
      setAssessments(prev => prev.filter(a => a.id !== assessmentId));
      setShowDeleteConfirm(null);
      alert('NREMT Assessment deleted successfully');
    } catch (error) {
      console.error('Error deleting assessment:', error);
      alert('Error deleting assessment. Please try again.');
    }
  };
  const handleDuplicate = async (assessment) => {
    if (!canCreate) return;

    try {
      console.log('🔄 Duplicating assessment:', assessment.name);
      
      const duplicatedAssessment = {
        ...assessment,
        name: `${assessment.name} (Copy)`,
        createdBy: currentUser?.email || 'Unknown',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Remove the id field
      delete duplicatedAssessment.id;
      
      console.log('🔄 Adding duplicated assessment to database...');
      const docRef = await addDoc(collection(db, 'nremtAssessments'), duplicatedAssessment);
      console.log('✅ Duplicated assessment created with ID:', docRef.id);
      
      // Add to local state instead of refetching everything
      setAssessments(prev => [...prev, {
        id: docRef.id,
        ...duplicatedAssessment
      }]);
      
      alert('NREMT Assessment duplicated successfully');
    } catch (error) {
      console.error('Error duplicating assessment:', error);
      alert('Error duplicating assessment. Please try again.');
    }
  };

  // Permission checks for access
  if (!currentUser || (userRole !== 'Instructor' && userRole !== 'Admin' && userRole !== 'owner')) {
    return (
      <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h1>
          <p className="text-lg">You don't have permission to access NREMT assessments.</p>
        </div>
      </div>
    );
  }

  if (userRole === 'Instructor' && (!permissions || permissions.skillSheets === 'none')) {
    return (
      <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">No Access</h1>
          <p className="text-lg">You don't have access to NREMT assessments. Please contact your administrator.</p>
          <button
            onClick={() => navigate(getDashboardPath())}
            className="mt-4 bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading || permissionsLoading) {
    return (
      <div className="min-h-screen bg-bg text-text flex items-center justify-center">
        <div className="text-lg">Loading NREMT assessments...</div>
      </div>
    );
  }

  // Show preview modal
  if (previewAssessment) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold">Preview: {previewAssessment.name}</h2>
            <button
              onClick={() => setPreviewAssessment(null)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
          <div className="p-4">
            <NREMTAssessmentPreview template={previewAssessment} showPDFButton={true} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-text py-8">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-primary">NREMT Assessment Builder 2.0</h1>
            <p className="text-gray-600 mt-1">Create and manage NREMT-style assessment forms</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate(getDashboardPath())}
              className="bg-gray-500 text-white px-4 py-2 rounded shadow hover:bg-gray-700 font-semibold transition duration-200"
            >
              ← Back to Dashboard
            </button>
            {canCreate && (
              <>
                <button
                  onClick={() => navigate(getCreatePath())}
                  className="bg-green-500 text-white px-6 py-2 rounded shadow hover:bg-green-700 font-semibold transition duration-200"
                >
                  📋 Create New Assessment
                </button>
                <button
                  onClick={() => navigate(getPrintBuilderPath())}
                  className="bg-purple-500 text-white px-6 py-2 rounded shadow hover:bg-purple-700 font-semibold transition duration-200"
                >
                  🖨️ Print Builder
                </button>
                <button
                  onClick={() => navigate(getManualSheetPath())}
                  className="bg-blue-500 text-white px-6 py-2 rounded shadow hover:bg-blue-700 font-semibold transition duration-200"
                >
                  📝 Manual Scenario Sheet
                </button>
              </>
            )}
          </div>
        </div>

        {/* Access Level Info */}
        {userRole === 'Instructor' && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-blue-800">Your Access Level</h3>
                <p className="text-sm text-blue-700">
                  {hasSpecialAccess ? 'Full access - create, edit, and delete' : 'General access - create and duplicate only'}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                hasSpecialAccess ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {hasSpecialAccess ? 'Special Assignment (Full Access)' : 'General Access'}
              </span>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-xl shadow-lg mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search assessments..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="sm:w-64">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Assessment Types</option>
                {assessmentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Assessments Grid */}
        {filteredAssessments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-2xl font-bold text-gray-600 mb-2">
              {search || selectedType ? 'No matching assessments found' : 'No NREMT assessments created yet'}
            </h2>
            <p className="text-gray-500 mb-6">
              {search || selectedType 
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first NREMT assessment to get started'
              }
            </p>
            {canCreate && !search && !selectedType && (
              <button
                onClick={() => navigate('/instructor/nremt-builder/create')}
                className="bg-green-500 text-white px-6 py-3 rounded-lg shadow hover:bg-green-700 font-semibold transition duration-200"
              >
                📋 Create Your First Assessment
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssessments.map((assessment) => (
              <div key={assessment.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-bold text-gray-800 flex-1">
                      {assessment.name || 'Untitled Assessment'}
                    </h3>
                    {canDelete && (
                      <button
                        onClick={() => setShowDeleteConfirm(assessment.id)}
                        className="text-red-500 hover:text-red-700 text-sm ml-2"
                        title="Delete assessment"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {assessment.assessmentType || 'Practical Skill'}
                    </span>
                    {assessment.timeLimit && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        {assessment.timeLimit} min
                      </span>
                    )}
                  </div>
                  
                  {assessment.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {assessment.description}
                    </p>
                  )}
                  
                  <div className="text-sm text-gray-500 mb-4">
                    <div>Rows: {assessment.rows?.length || 0}</div>
                    <div>Critical: {assessment.rows?.filter(r => r.critical).length || 0}</div>
                    <div>Created by: {assessment.createdBy || 'Unknown'}</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewAssessment(assessment)}
                    className="flex-1 bg-blue-500 text-white px-3 py-2 rounded shadow hover:bg-blue-700 font-semibold transition duration-200 text-sm"
                  >
                    👁️ Preview
                  </button>
                  <button
                    onClick={() => navigate(getPrintPath(assessment.id))}
                    className="bg-purple-500 text-white px-3 py-2 rounded shadow hover:bg-purple-700 font-semibold transition duration-200 text-sm"
                    title="Generate print form"
                  >
                    🖨️ Print
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => navigate(getEditPath(assessment.id))}
                      className="bg-green-500 text-white px-3 py-2 rounded shadow hover:bg-green-700 font-semibold transition duration-200 text-sm"
                    >
                      ✏️ Edit
                    </button>
                  )}
                  <button
                    onClick={() => handleDuplicate(assessment)}
                    className="bg-orange-500 text-white px-3 py-2 rounded shadow hover:bg-orange-700 font-semibold transition duration-200 text-sm"
                    title="Duplicate assessment"
                  >
                    📄
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this NREMT assessment? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded shadow hover:bg-gray-700 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 bg-red-500 text-white px-4 py-2 rounded shadow hover:bg-red-700 transition duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-8 bg-white p-4 rounded-xl shadow-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Quick Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{assessments.length}</div>
              <div className="text-sm text-gray-600">Total Assessments</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {assessments.filter(a => a.assessmentType === 'Practical Skill').length}
              </div>
              <div className="text-sm text-gray-600">Practical Skills</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {assessments.filter(a => a.assessmentType === 'Patient Assessment').length}
              </div>
              <div className="text-sm text-gray-600">Patient Assessments</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">
                {assessments.reduce((sum, a) => sum + (a.rows?.length || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Total Rows</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default NREMTAssessmentList;
